// OP-3605 — Ocorrências (ver ESL1; resolver ESL12): o que a conciliação
// noturna encontrou comparando o razão com a venda real.
//
// Duas naturezas, e a diferença importa: ALERTA é algo errado, DECISÃO é algo
// que só espera alguém decidir. E duas formas de linha: a gravada, que se
// resolve aqui, e a derivada de um pedido, que não tem o que resolver — ela
// some quando o saldo zera, e o caminho é o próprio pedido.
import { FC, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueOcorrencias } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { resolverEstoqueOcorrencia } from '../../../../Service/estoque'
import { NaturezaOcorrencia, Ocorrencia, OcorrenciaGravada, Resolucao } from '../../../../types/estoque'
import {
  BarraChips, BD, Button, Card, Chip, Field, FiltersRow, Folha, FolhaOverlay, FolhaPega, FolhaTopo,
  Input, PageSubtitle, PageTitle, Select, SoCelular, SoDesktop, Spinner, StatusBadge,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { tratarErro } from '../erros'
import {
  NATUREZA_UI, RESOLUCOES, TIPO_OCORRENCIA_UI, ehDerivada, impedimentoResolver, ordenarPorNatureza,
  podeResolver, resolucaoSilencia,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

export const EstoqueOcorrencias: FC = () => {
  const navigate = useNavigate()
  const { pode } = usePerfilEstoque()
  const podeOcorrencias = pode('OCORRENCIAS')
  const [natureza, setNatureza] = useState<NaturezaOcorrencia | ''>('')
  const { data, isLoading, isError, isUnauthorized, mutate } = useEstoqueOcorrencias(natureza)
  const [resolvendo, setResolvendo] = useState<OcorrenciaGravada | null>(null)
  const [resolucao, setResolucao] = useState<Resolucao | ''>('')
  const [observacao, setObservacao] = useState('')
  const [busy, setBusy] = useState(false)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const fechar = () => { setResolvendo(null); setResolucao(''); setObservacao('') }

  const resolver = async () => {
    if (!resolvendo || !resolucao) return
    setBusy(true)
    try {
      await resolverEstoqueOcorrencia({ id: resolvendo.id, resolucao, observacao: observacao.trim() || null })
      toast.success('Ocorrência resolvida')
      fechar()
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  const linha = (o: Ocorrencia, k: number) => {
    const nat = NATUREZA_UI[o.natureza]
    const tipo = TIPO_OCORRENCIA_UI[o.tipo]
    return (
      <Card key={`${o.tipo}-${o.id ?? 'd'}-${k}`} style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px' }}>
            <div style={{ marginBottom: 4 }}>
              <StatusBadge $bg={nat.bg} $color={nat.color}>{nat.label}</StatusBadge>{' '}
              <b>{tipo?.label ?? o.tipo}</b>
            </div>
            <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 6 }}>{tipo?.explica}</div>

            {ehDerivada(o) ? (
              <div style={{ fontSize: 13 }}>
                Pedido <b>{o.numero}</b>{o.detentorNome ? ` · ${o.detentorNome}` : ''}{o.clienteNome ? ` · ${o.clienteNome}` : ''}
                <div style={{ color: BD.grayText }}>pedido {o.pedido} · saiu {o.atendido} · <b>faltam {o.pendente}</b></div>
              </div>
            ) : (
              <div style={{ fontSize: 13 }}>
                <span style={{ fontFamily: 'monospace' }}>{o.serial}</span>
                <span> · {o.itemNome}</span>
                {o.detentorNome && <span> · com {o.detentorNome}</span>}
                {o.remessaCodigo && <span> · remessa {o.remessaCodigo}</span>}
                <div style={{ color: BD.grayText }}>
                  detectada {fmtDateTime(o.detectadoEm)}
                  {o.vistoEm && <> · reconfirmada {fmtDateTime(o.vistoEm)}</>}
                  {o.dataReferencia && <> · limite {fmtDate(o.dataReferencia)}</>}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            {ehDerivada(o) ? (
              <Button $variant="secondary" onClick={() => navigate(`/interno/estoque/fila/${o.solicitacaoId}`)}>Abrir o pedido</Button>
            ) : podeResolver(o, podeOcorrencias) ? (
              <Button $variant="secondary" disabled={busy} onClick={() => { setResolvendo(o); setResolucao(''); setObservacao('') }}>Resolver</Button>
            ) : null}
          </div>
        </div>
      </Card>
    )
  }

  const formResolucao = () => (
    <>
      <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
        A linha fica no histórico com o que foi decidido. Cobrado e ignorado silenciam a varredura enquanto o prazo não mudar; regularizado e prorrogado só valem se o fato tiver mudado — se não mudou, a pendência volta amanhã.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <Field>
          <label>O que foi feito</label>
          <Select value={resolucao} onChange={(e) => setResolucao(e.target.value as Resolucao | '')}>
            <option value="">— escolha —</option>
            {RESOLUCOES.map((r) => <option key={r.resolucao} value={r.resolucao}>{r.rotulo}</option>)}
          </Select>
          {resolucao && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>{RESOLUCOES.find((r) => r.resolucao === resolucao)?.efeito}</div>}
        </Field>
        <Field><label>Observação (opcional)</label><Input value={observacao} maxLength={500} onChange={(e) => setObservacao(e.target.value)} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button disabled={busy || !!impedimentoResolver(resolucao, podeOcorrencias)} onClick={resolver}>Registrar decisão</Button>
        <Button $variant="secondary" disabled={busy} onClick={fechar}>Fechar</Button>
        {resolucaoSilencia(resolucao) && <span style={{ fontSize: 12, color: BD.grayText }}>Esta resolução silencia a pendência enquanto o prazo for o mesmo.</span>}
      </div>
    </>
  )

  const linhas = ordenarPorNatureza(data)
  const alertas = linhas.filter((o) => o.natureza === 'ALERTA').length

  return (
    <>
      <PageTitle>Ocorrências</PageTitle>
      <PageSubtitle>
        O que a conciliação encontrou comparando o razão com a venda real. Alerta é divergência a investigar; decisão é algo que espera alguém escolher o que fazer. A lista é curta de propósito: sem lastro, a rotina cala.
      </PageSubtitle>

      <BarraChips role="group" aria-label="Natureza da ocorrência">
        <Chip type="button" $ativo={!natureza} onClick={() => setNatureza('')}>Todas</Chip>
        <Chip type="button" $ativo={natureza === 'ALERTA'} onClick={() => setNatureza('ALERTA')}>Alerta</Chip>
        <Chip type="button" $ativo={natureza === 'DECISAO'} onClick={() => setNatureza('DECISAO')}>Decisão</Chip>
        <Chip type="button" onClick={() => mutate()}>↻ Atualizar</Chip>
      </BarraChips>

      <SoDesktop>
        <FiltersRow>
          <Field>
            <label>Natureza</label>
            <Select value={natureza} onChange={(e) => setNatureza(e.target.value as NaturezaOcorrencia | '')}>
              <option value="">todas</option>
              <option value="ALERTA">Alerta — algo está errado</option>
              <option value="DECISAO">Decisão — falta decidir</option>
            </Select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}><Button $variant="secondary" onClick={() => mutate()}>Atualizar</Button></div>
        </FiltersRow>
      </SoDesktop>

      {resolvendo && (
        <SoDesktop>
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Resolver: {TIPO_OCORRENCIA_UI[resolvendo.tipo]?.label ?? resolvendo.tipo} · {resolvendo.serial}</div>
            {formResolucao()}
          </Card>
        </SoDesktop>
      )}

      {resolvendo && (
        <SoCelular>
          <FolhaOverlay $open onClick={fechar} />
          <Folha $open role="dialog" aria-label="Resolver ocorrência">
            <FolhaPega />
            <FolhaTopo>
              <h2>{TIPO_OCORRENCIA_UI[resolvendo.tipo]?.label ?? resolvendo.tipo}</h2>
            </FolhaTopo>
            <div style={{ fontFamily: 'monospace', fontSize: 13, color: BD.grayText, marginBottom: 12 }}>{resolvendo.serial}</div>
            {formResolucao()}
          </Folha>
        </SoCelular>
      )}

      {isLoading ? <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
        : isError ? (
          <Card><div style={{ padding: 24, color: BD.red }}>
            Não foi possível carregar as ocorrências. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
          </div></Card>
        ) : linhas.length === 0 ? (
          <Card><div style={{ padding: 24, color: BD.grayText }}>
            Nenhuma ocorrência aberta{natureza ? ' desta natureza' : ''}. Lista vazia aqui é notícia boa: o razão e a venda estão contando a mesma história.
          </div></Card>
        ) : (
          <>
            <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
              {linhas.length} {linhas.length === 1 ? 'ocorrência' : 'ocorrências'}{alertas > 0 ? ` · ${alertas} de alerta` : ''}
            </div>
            {linhas.map(linha)}
          </>
        )}
      {!podeOcorrencias && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>Resolver ocorrência exige a permissão "Ocorrências".</div>}
    </>
  )
}

export default EstoqueOcorrencias
